<template>
	<div class="date-strip">
		<div v-if="daysInRange.length > 1" class="date-strip__month">{{ currentMonthLabel }}</div>
		<div v-else class="date-strip__month">{{ timeSession }}</div>
		<div v-if="daysInRange.length > 1" class="date-strip__days">
			<button
				v-if="isScrollable"
				class="date-strip__button"
				type="button"
				:disabled="!canPrev"
				@click="tryShift(-1)"
			>
				<Icon type="chevron_left" :size="14" color="var(--button-icon-tint)" />
			</button>
			<RecycleScroller
				v-if="isScrollable"
				ref="scrollContainer"
				class="date-strip__days-wrapper"
				:items="daysInRange"
				key-field="iso"
				direction="horizontal"
				:item-size="ITEM_SIZE"
				:buffer="300"
			>
				<template #default="{ item }">
					<div
						class="date-strip__days-wrapper__button"
						:data-iso="item.iso"
						@click="$emit('update:modelValue', item.iso)"
					>
						<div class="date-strip__days-wrapper__button__week-day">
							{{ item.dow }}
						</div>

						<div
							:class="{ active: item.iso === modelValue }"
							class="date-strip__days-wrapper__button__day"
						>
							{{ item.day }}
						</div>

						<div style="width: 4px; height: 4px">
							<div
								v-if="markerDates.has(item.iso)"
								class="date-strip__days-wrapper__button__week-day__marker"
							/>
						</div>
					</div>
				</template>
			</RecycleScroller>
			<div v-else class="date-strip__one-week">
				<div
					v-for="item in daysInRange"
					:key="item.iso"
					style="margin-right: 0"
					class="date-strip__one-week__button"
					:data-iso="item.iso"
					@click="$emit('update:modelValue', item.iso)"
				>
					<div class="date-strip__days-wrapper__button__week-day">
						{{ item.dow }}
					</div>

					<div
						:class="{ active: item.iso === modelValue }"
						class="date-strip__one-week__button__day"
					>
						{{ item.day }}
					</div>

					<div style="width: 4px; height: 4px">
						<div
							v-if="markerDates.has(item.iso)"
							class="date-strip__days-wrapper__button__week-day__marker"
						/>
					</div>
				</div>
			</div>
			<button
				v-if="isScrollable"
				class="date-strip__button"
				type="button"
				:disabled="!canNext"
				@click="tryShift(+1)"
			>
				<Icon type="chevron_right" :size="14" color="var(--button-icon-tint)" />
			</button>
		</div>
	</div>
</template>

<script>
import { utcTsToIsoInEventTz, eventTsToUTCDate, utcTsToIsoDayInEventTz } from '@/utils/eventDate'
export default {
	name: 'AgendaDateStrip',
	props: {
		modelValue: { type: String, required: true },
		weekStart: { type: Number, default: 1 },
		isStart: { type: [Number, String], default: null },
		isEnd: { type: [Number, String], default: null },
		dateListEvent: { type: Array, default: () => [] },
		timezoneUtc: { type: String, default: 'UTC+0' },
	},

	data() {
		return {
			anchor: null,
			ITEM_SIZE: 67,
			_measured: false,
			_scrollRaf: 0,
		}
	},

	computed: {
		isScrollable() {
			return this.daysInRange.length > 7
		},
		currentMonthLabel() {
			const d = this.anchor
			if (!d) return ''
			return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()
		},
		timeSession() {
			if (this.daysInRange.length > 1) return
			const date = eventTsToUTCDate(this.isStart, this.timezoneUtc)

			const weekday = date
				.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' })
				.toUpperCase()
			const day = date.getUTCDate()
			const month = date
				.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })
				.toUpperCase()
			const year = date.getUTCFullYear()

			return `${weekday} ${day}, ${month} ${year}`
		},

		maxDate() {
			if (!this.isEnd) return null
			const isoDay = utcTsToIsoDayInEventTz(this.isEnd, this.timezoneUtc)
			const [y, m, d] = isoDay.split('-').map(Number)
			return new Date(Date.UTC(y, m - 1, d))
		},

		minDate() {
			if (!this.isStart) return null
			const isoDay = utcTsToIsoDayInEventTz(this.isStart, this.timezoneUtc)
			const [y, m, d] = isoDay.split('-').map(Number)
			return new Date(Date.UTC(y, m - 1, d))
		},

		daysInRange() {
			if (!this.minDate || !this.maxDate) return []
			const days = []
			for (
				let d = new Date(this.minDate);
				d <= this.maxDate;
				d.setUTCDate(d.getUTCDate() + 1)
			) {
				const copy = new Date(d)
				days.push({
					date: copy,
					iso: this.toISO(copy),
					dow: copy
						.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
						.toUpperCase(),
					day: copy.getUTCDate(),
				})
			}
			return days
		},

		markerDates() {
			return new Set(
				this.dateListEvent.map((m) =>
					utcTsToIsoInEventTz(m.events_agenda_from, this.timezoneUtc)
				)
			)
		},

		canPrev() {
			return this.minDate ? (this.anchor ? this.anchor > this.minDate : true) : false
		},
		canNext() {
			return this.maxDate ? (this.anchor ? this.anchor < this.maxDate : true) : false
		},
	},

	watch: {
		modelValue(val) {
			if (!val) return
			const parsed = this.parseISO(val)
			if (!this.isInRange(parsed)) {
				const firstIso = this.toISO(this.minDate)
				this.$emit('update:modelValue', firstIso)
				this.anchor = this.minDate
			} else {
				this.anchor = this.clampDate(parsed)
			}
			this.$nextTick(() => this.scrollToActive())
		},
		isStart() {
			this.$nextTick(() => this.scrollToActive())
		},
		isEnd() {
			this.$nextTick(() => this.scrollToActive())
		},
		daysInRange() {
			this.$nextTick(() => this.scrollToActive())
		},
	},

	mounted() {
		const initial = this.parseISO(this.modelValue)
		if (!this.isInRange(initial)) {
			const firstIso = this.toISO(this.minDate)
			this.$emit('update:modelValue', firstIso)
			this.anchor = this.minDate
		} else {
			this.anchor = this.clampDate(initial)
		}
		this.$nextTick(() => {
			this.measureItemSizeOnce()
			this.attachNativeScroll()
			requestAnimationFrame(() => this.scrollToActive())
		})
	},

	beforeDestroy() {
		this.detachNativeScroll()
		if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
	},

	methods: {
		toISO(d) {
			const y = d.getUTCFullYear()
			const m = String(d.getUTCMonth() + 1).padStart(2, '0')
			const day = String(d.getUTCDate()).padStart(2, '0')
			return `${y}-${m}-${day}`
		},
		parseISO(iso) {
			if (!iso || typeof iso !== 'string') return new Date(NaN)

			const [y, m, d] = iso.split('-').map(Number)

			return new Date(Date.UTC(y, m - 1, d))
		},
		startOfDay(d) {
			const x = new Date(d)
			x.setHours(0, 0, 0, 0)
			return x
		},
		isInRange(d) {
			const min = this.minDate
			const max = this.maxDate
			if (!min || !max || !d) return true
			const x = this.startOfDay(d)
			return x >= min && x <= max
		},
		clampDate(d) {
			const x = this.startOfDay(d)
			if (this.minDate && x < this.minDate) return new Date(this.minDate)
			if (this.maxDate && x > this.maxDate) return new Date(this.maxDate)
			return x
		},
		attachNativeScroll() {
			const scroller = this.$refs.scrollContainer
			const el = scroller && scroller.$el
			if (!el) return
			el.addEventListener('scroll', this.onNativeScroll, { passive: true })
			this._scrollEl = el
		},
		detachNativeScroll() {
			if (this._scrollEl) {
				this._scrollEl.removeEventListener('scroll', this.onNativeScroll)
			}
			this._scrollEl = null
		},

		onNativeScroll() {
			if (this._scrollRaf) return
			this._scrollRaf = requestAnimationFrame(() => {
				this._scrollRaf = 0
				const el = this._scrollEl
				if (!el || !Number.isFinite(this.ITEM_SIZE) || this.ITEM_SIZE <= 0) return

				const scrollLeft = el.scrollLeft
				const centerIndex = Math.min(
					this.daysInRange.length - 1,
					Math.max(0, Math.round((scrollLeft + el.clientWidth / 2) / this.ITEM_SIZE))
				)

				const day = this.daysInRange[centerIndex]
				if (!day) return

				const date = this.parseISO(day.iso)
				this.anchor = this.startOfDay(date)
			})
		},

		scrollToActive() {
			const scroller = this.$refs.scrollContainer
			const el = scroller && scroller.$el
			if (!el || !Number.isFinite(this.ITEM_SIZE)) return
			const idx = this.daysInRange.findIndex((d) => d.iso === this.modelValue)
			this.scrollToIndex(idx >= 0 ? idx : 0, true)
		},

		scrollToIndex(index, center = true) {
			const scroller = this.$refs.scrollContainer
			const el = scroller && scroller.$el
			if (!el || !Number.isFinite(this.ITEM_SIZE)) return

			const target =
				index * this.ITEM_SIZE - (center ? (el.clientWidth - this.ITEM_SIZE) / 2 : 0)

			if (typeof scroller.scrollToPosition === 'function') {
				scroller.scrollToPosition(Math.max(0, target))
			} else {
				el.scrollTo({ left: Math.max(0, target), behavior: 'auto' })
			}
		},

		tryShift(pagesDelta) {
			const el = this.$refs.scrollContainer?.$el
			if (!el) return
			el.scrollBy({
				left: pagesDelta * el.clientWidth,
				behavior: 'smooth',
			})
		},
		measureItemSizeOnce() {
			if (this._measured) return
			const el = this.$el.querySelector('.date-strip__days-wrapper__button')
			if (!el) return
			const rect = el.getBoundingClientRect()
			const mr = parseFloat(getComputedStyle(el).marginRight || '0')
			const size = Math.round(rect.width + mr)
			if (size > 0) this.ITEM_SIZE = size
			this._measured = true
		},
	},
}
</script>

<style lang="scss">
.date-strip {
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 555px;
	width: 100%;
	font-family: 'Inter', sans-serif;
	gap: 8px;
	&__one-week {
		display: flex;
		flex-direction: row;
		justify-content: center;
		width: 100%;
		gap: 20px;
		&__button {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			width: 100%;
			gap: 4px;
			&__day {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 100%;
				height: 40px;
				border-radius: 8px;
				background-color: #ffffff;
				border: 1.5px solid #e2e2e2;
				cursor: pointer;
				transition: all 0.2s ease;

				&.active {
					background-color: var(--accent-color);
					color: #ffffff;
				}
			}
		}
	}

	&__button {
		width: 32px;
		height: 32px;
		padding: 4px;
	}

	&__month {
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		text-align: center;
		color: var(--accent-color);
		transition: all 0.2s ease;
	}

	&__days {
		display: flex;
		width: 100%;
		max-width: 555px;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
	}
	&__days-wrapper {
		display: block;
		overflow-x: auto;
		padding: 0 4px;
		max-width: 451px;
		width: 100%;
		height: 72px;
		scrollbar-width: none;
		-ms-overflow-style: none;
		&::-webkit-scrollbar {
			display: none;
		}
		&__flex {
			display: flex;
			justify-content: space-between;
		}

		&__button {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 4px;
			width: 40px;
			flex-shrink: 0;
			margin-right: 27px;

			&__week-day {
				font-weight: 700;
				font-size: 14px;
				line-height: 100%;
				text-align: center;
				color: var(--accent-color);

				&__marker {
					width: 4px;
					height: 4px;
					background-color: var(--accent-color);
					border-radius: 50%;
					margin-top: 4px;
				}
			}

			&__day {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 40px;
				height: 40px;
				border-radius: 8px;
				background-color: #ffffff;
				border: 1.5px solid #e2e2e2;
				cursor: pointer;
				transition: all 0.2s ease;

				&.active {
					background-color: var(--accent-color);
					color: #ffffff;
				}
			}
		}
	}
}
</style>
